import { useState } from "react";
import { Laugh, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useUser } from "@clerk/react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";

// Custom Star Eyes Face SVG Icon
const StarEyesIcon = ({ className, ...props }: React.ComponentProps<"svg">) => (
  <svg
    viewBox="0 0 16 16"
    data-slot="geist-icon"
    style={{ color: "currentColor" }}
    className={className}
    {...props}
  >
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M14.5 8a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-11.5.97h-.62v.63c0 1.87 1.93 3.26 4.12 3.26s4.13-1.38 4.13-3.26v-.63H4.5M8 11.61c-1.4 0-2.36-.66-2.72-1.38h5.44c-.36.72-1.31 1.38-2.72 1.38"
      clipRule="evenodd"
    />
    <path
      fill="#d97706"
      fillRule="evenodd"
      d="M6.15 4.92 5.37 3.5 4.6 4.92l-1.6.3 1.12 1.17L3.9 8l1.47-.7 1.46.7-.2-1.6 1.11-1.18zm5.25 0-.78-1.42-.77 1.42-1.6.3 1.12 1.17L9.16 8l1.47-.7 1.46.7-.2-1.6L13 5.21z"
      clipRule="evenodd"
    />
  </svg>
);

// Custom Smile Face SVG Icon
const SmileIcon = ({ className, ...props }: React.ComponentProps<"svg">) => (
  <svg
    viewBox="0 0 16 16"
    data-slot="geist-icon"
    style={{ color: "currentColor" }}
    className={className}
    {...props}
  >
    <path
      fill="currentColor"
      d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0m0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13m3.87 8.83-.35.52a4.24 4.24 0 0 1-7.04 0l-.35-.51 1.04-.7.35.52a3 3 0 0 0 4.97 0l.34-.53zM5.75 5.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5m4.5 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5"
    />
  </svg>
);

// Custom Frown Face SVG Icon
const FrownIcon = ({ className, ...props }: React.ComponentProps<"svg">) => (
  <svg
    viewBox="0 0 16 16"
    data-slot="geist-icon"
    style={{ color: "currentColor" }}
    className={className}
    {...props}
  >
    <path
      fill="currentColor"
      d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0m0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13m0 7.88c1.47 0 2.76.75 3.52 1.88l.35.52-1.04.7-.34-.52a3 3 0 0 0-4.97 0l-.35.51-1.04-.7.35-.51A4.2 4.2 0 0 1 8 9.38M5.75 5.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5m4.5 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5"
    />
  </svg>
);

// Custom Support Headset Face SVG Icon
const SupportIcon = ({ className, ...props }: React.ComponentProps<"svg">) => (
  <svg
    viewBox="0 0 16 16"
    data-slot="geist-icon"
    style={{ color: "currentColor" }}
    className={className}
    {...props}
  >
    <path
      fill="#2563eb"
      fillRule="evenodd"
      d="M4 9v7h1.5V9zm8 0v7h-1.5V9z"
      clipRule="evenodd"
    />
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M1.5 8A6.5 6.5 0 1 1 13 12.15v2.1A7.99 7.99 0 0 0 8 0a8 8 0 0 0-5 14.25v-2.1A6.5 6.5 0 0 1 1.5 8M8 14.5q.78 0 1.5-.17v1.53a8 8 0 0 1-3 0v-1.53q.72.17 1.5.17M3.79 8.37a2.04 2.04 0 0 1 2.92 0L7.8 7.32a3.54 3.54 0 0 0-5.08 0zm6.96-.62c-.57 0-1.1.23-1.46.62L8.2 7.32a3.54 3.54 0 0 1 5.08 0L12.2 8.37a2 2 0 0 0-1.46-.62M6.25 12h3.5a1.75 1.75 0 1 0-3.5 0"
      clipRule="evenodd"
    />
  </svg>
);

const FEEDBACK_OPTIONS = [
  {
    id: "love",
    label: "Love it!",
    icon: StarEyesIcon,
    activeColor: "text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300",
  },
  {
    id: "good",
    label: "Good",
    icon: SmileIcon,
    activeColor: "text-emerald-500 hover:text-emerald-600 dark:text-emerald-400 dark:hover:text-emerald-300",
  },
  {
    id: "bad",
    label: "Bad",
    icon: FrownIcon,
    activeColor: "text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300",
  },
  {
    id: "support",
    label: "Need Help",
    icon: SupportIcon,
    activeColor: "text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300",
  },
];

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { user } = useUser();
  const submitFeedback = useAction(api.feedback.submitFeedback);

  const dismissFeedbackMetadata = async () => {
    if (user && (user.unsafeMetadata?.feedbackdialog === "true" || user.unsafeMetadata?.feedbackdialog === true)) {
      try {
        await user.update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            feedbackdialog: "false",
          },
        });
      } catch (error) {
        console.error("Failed to update Clerk user metadata:", error);
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedOption) return;
    setIsSubmitting(true);
    try {
      const userEmail = user?.emailAddresses[0]?.emailAddress || "anonymous@lurna.co";
      await submitFeedback({
        userEmail,
        reaction: selectedOption,
        comment,
      });

      await dismissFeedbackMetadata();
      setIsSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (err: any) {
      console.error("Error submitting feedback:", err);
      toast.error(err.message || "Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (!isSubmitting) {
      await dismissFeedbackMetadata();
      onOpenChange(false);
      // Reset state after transition completes
      setTimeout(() => {
        setSelectedOption(null);
        setComment("");
        setIsSubmitted(false);
      }, 300);
    }
  };

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="md:max-w-[30vw]">
          <DialogTitle className="flex items-center gap-2"><Laugh /> Thank you!</DialogTitle>
          <DialogDescription className="mt-2">
            Your feedback has been submitted successfully. We appreciate you taking the time to share your experience with us!
          </DialogDescription>
          <div className="mt-6 w-full">
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="md:max-w-[30vw]">
        <DialogHeader className="">
          <DialogTitle className="">
            How do you like Lurna so far?
          </DialogTitle>
          <DialogDescription className="">
            Help us improve by sharing your thoughts and experience.
          </DialogDescription>
        </DialogHeader>

        {/* Four Reactions Row */}
        <div className="grid grid-cols-4 gap-2 py-4 my-2">
          {FEEDBACK_OPTIONS.map((option) => {
            const IconComponent = option.icon;
            const isSelected = selectedOption === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setSelectedOption(option.id)}
                className={cn(
                  "flex flex-col items-center justify-center p-3 rounded-2xl transition-all duration-300 gap-2 border",
                  "hover:bg-neutral-100 dark:hover:bg-neutral-800/60 active:scale-95 cursor-pointer outline-none",
                  isSelected
                    ? "bg-neutral-100 dark:bg-neutral-800 border-neutral-250 dark:border-neutral-700 shadow-xs scale-105 font-semibold"
                    : "border-transparent opacity-70 hover:opacity-100"
                )}
              >
                <div
                  className={cn(
                    "w-10 h-10 transition-all duration-300 text-neutral-400 dark:text-neutral-500",
                    isSelected ? option.activeColor : "hover:text-neutral-600 dark:hover:text-neutral-300"
                  )}
                >
                  <IconComponent className="w-full h-full" />
                </div>
                <span className="text-[11px] font-medium text-muted-foreground leading-none text-center">
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Comment Textarea */}
        <div className="">
          <Textarea
            id="feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              selectedOption === "support"
                ? "What can we help you with?"
                : selectedOption === "bad"
                  ? "What went wrong or what can we improve?"
                  : "Anything else you'd like to share?"
            }
            className="min-h-[100px] max-h-[180px] "
            disabled={isSubmitting}
          />
        </div>

        <DialogFooter className="mt-6 flex flex-row justify-end gap-2 w-full">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedOption || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
